import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { PermissionGuard } from 'app/core/permissions/permissions.guard';
import { TabbedPageLayoutComponent } from 'app/layout/page-layouts/tabbed/tabbed-page-layout.component';
import { templateResolver } from './resolvers/template/template.resolver';
import { marketsResolver } from './resolvers/markets/markets.resolver';
import { StoryListComponent } from './pages/stories/platform-stories.component';
import { PaymentMethodListComponent } from './pages/payment-methods/payment-method-list.component';
import { BannersComponent } from './pages/banners/banners.component';

export const settingsRoutes: Route[] = [
    {
        path: 'settings',
        canActivate: [AuthGuard],
        component: LayoutComponent,
        children: [
            {
                path: '',
                redirectTo: 'platform',
                pathMatch: 'full',
            },
            {
                path: 'template/markets',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            breadcrumbs: [
                                {
                                    label: 'settings.template-editor.markets.title',
                                },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './pages/template-editor/markets/template-markets.component'
                            ).then((c) => c.TemplateMarketsComponent),
                    },
                    {
                        path: ':sport',
                        resolve: { breadcrumb: marketsResolver },
                        loadComponent: () =>
                            import(
                                './pages/template-editor/choose-markets/choose-markets.component'
                            ).then((c) => c.ChooseMarketsComponent),
                    },
                ],
            },
            {
                path: 'template/editor',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            breadcrumbs: [
                                { label: 'settings.template-editor.title' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './pages/template-editor/sports/template-editor.component'
                            ).then((c) => c.TemplateEditorComponent),
                    },
                    {
                        path: ':sport',
                        resolve: { breadcrumb: templateResolver },
                        loadComponent: () =>
                            import(
                                './pages/template-editor/layout-builder/layout-builder.component'
                            ).then((c) => c.LayoutBuilderComponent),
                    },
                ],
            },
            {
                path: 'stories',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        canActivate: [PermissionGuard],
                        data: {
                            breadcrumbs: [{ label: 'settings.stories.title' }],
                            permission: 'READ_STORY',
                            actions: [
                                {
                                    label: 'settings.stories.add-story',
                                    color: 'primary',
                                    execute: (
                                        componentInstance: StoryListComponent
                                    ) => {
                                        componentInstance.addStory();
                                    },
                                    permission: 'CREATE_STORY',
                                },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './pages/stories/platform-stories.component'
                            ).then((c) => c.StoryListComponent),
                    },
                ],
            },
            {
                path: 'offers',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        canActivate: [PermissionGuard],
                        data: {
                            breadcrumbs: [{ label: 'settings.offers.title' }],
                        },
                        loadComponent: () =>
                            import('./pages/offers/offers.component').then(
                                (c) => c.OfferListComponent
                            ),
                    },
                ],
            },
            {
                path: 'banners',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        canActivate: [PermissionGuard],
                        data: {
                            breadcrumbs: [{ label: 'settings.banners.title' }],
                            permission: 'READ_BANNER',
                            actions: [
                                {
                                    label: 'settings.banners.add-banner',
                                    color: 'primary',
                                    execute: (
                                        componentInstance: BannersComponent
                                    ) => {
                                        componentInstance.addBanner();
                                    },
                                    permission: 'CREATE_BANNER',
                                },
                            ],
                        },
                        loadComponent: () =>
                            import('./pages/banners/banners.component').then(
                                (c) => c.BannersComponent
                            ),
                    },
                ],
            },
            {
                path: 'notifications',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        canActivate: [PermissionGuard],
                        data: {
                            breadcrumbs: [
                                { label: 'settings.notifications.title' },
                            ],
                            permission: 'READ_NOTIFICATION_CONFIG',
                            actions: [
                                {
                                    label: '',
                                    icon: 'heroicons_outline:cog-6-tooth',
                                    execute: (componentInstance) => {
                                        componentInstance.openTableColumnSettings();
                                    },
                                },
                                {
                                    label: 'actions.new',
                                    route: '/settings/notifications/new',
                                    color: 'primary',
                                },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './pages/notifications/notifications.component'
                            ).then((c) => c.NotificationsComponent),
                    },
                    {
                        path: 'new',
                        canActivate: [PermissionGuard],
                        data: {
                            permission: 'CREATE_NOTIFICATION_CONFIG',
                            breadcrumbs: [
                                {
                                    label: 'settings.notifications.title',
                                    link: '/settings/notifications',
                                },
                                {
                                    label: 'settings.notifications.new-notification',
                                },
                            ],
                            mode: 'new',
                        },
                        loadComponent: () =>
                            import(
                                './components/notification-form/notification-form.component'
                            ).then((c) => c.NotificationFormComponent),
                    },
                    {
                        path: 'edit/:id',
                        canActivate: [PermissionGuard],
                        data: {
                            permission: 'UPDATE_NOTIFICATION_CONFIG',
                            breadcrumbs: [
                                {
                                    label: 'settings.notifications.title',
                                    link: '/settings/notifications',
                                },
                                {
                                    label: 'settings.notifications.edit-notification',
                                },
                            ],
                            mode: 'edit',
                        },
                        loadComponent: () =>
                            import(
                                './components/notification-form/notification-form.component'
                            ).then((c) => c.NotificationFormComponent),
                    },
                ],
            },
            {
                path: 'highlights',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        canActivate: [PermissionGuard],
                        data: {
                            breadcrumbs: [
                                { label: 'settings.highlights.title' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './pages/highlights/highlights.component'
                            ).then((c) => c.HighlightsComponent),
                    },
                ],
            },
            {
                path: 'payment-providers',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        canActivate: [PermissionGuard],
                        data: {
                            breadcrumbs: [
                                { label: 'settings.payment.methods.title' },
                            ],
                            actions: [
                                {
                                    label: 'settings.payment.methods.add',
                                    execute: (
                                        componentInstance: PaymentMethodListComponent
                                    ) => {
                                        componentInstance.addPaymentMethod();
                                    },
                                },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './pages/payment-methods/payment-method-list.component'
                            ).then((c) => c.PaymentMethodListComponent),
                    },
                ],
            },
            {
                path: 'casino-management',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            breadcrumbs: [
                                { label: 'settings.casino-management.title' },
                            ],
                            actions: [
                                {
                                    label: '',
                                    icon: 'heroicons_outline:cog-6-tooth',
                                    execute: (componentInstance) => {
                                        componentInstance.openTableColumnSettings();
                                    },
                                },
                                {
                                    label: 'actions.export',
                                    icon: 'heroicons_outline:document-arrow-down',
                                    execute: (componentInstance) => {
                                        componentInstance.exportTableData();
                                    },
                                },
                            ],
                            showBulkActionButton: false,
                            rowDependentActions: [
                                {
                                    label: 'settings.casino-management.bulk.manageGames',
                                    icon: 'heroicons_outline:adjustments-horizontal',
                                    execute: (componentInstance) => {
                                        componentInstance.openBulkActionDialog();
                                    },
                                },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './pages/casino-management/casino-management.component'
                            ).then((c) => c.CasinoManagementComponent),
                    },
                ],
            },
            {
                path: 'configurations',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        data: {
                            breadcrumbs: [
                                { label: 'settings.configurations.title' },
                            ],
                        },
                        loadComponent: () =>
                            import(
                                './pages/configurations/configurations.component'
                            ).then((c) => c.ConfigurationsComponent),
                    },
                ],
            },
        ],
    },

    /*settings in profile icon in navigation bar*/
    {
        path: 'settings',
        canActivate: [AuthGuard],
        component: LayoutComponent,
        data: {
            breadcrumbs: [{ label: 'settings.title' }],
            tabs: [
                { label: 'settings.profile.title', route: 'profile' },
                {
                    label: 'settings.change-password.title',
                    route: 'change-password',
                },
            ],
            mode: 'edit',
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'profile',
            },
            {
                path: '',
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        data: {
                            actions: [
                                {
                                    label: 'actions.edit',
                                    route: '/settings/profile/edit',
                                    color: 'primary',
                                },
                            ],
                        },
                        path: 'profile',
                        loadComponent: () =>
                            import(
                                '../settings/pages/profile/profile.component'
                            ).then((c) => c.ProfileComponent),
                    },
                    {
                        data: {
                            breadcrumbs: [
                                {
                                    label: 'settings.title',
                                    link: '/settings/profile',
                                },
                                {
                                    label: 'settings.profile.edit-profile.title',
                                },
                            ],
                        },
                        path: 'profile/edit',
                        loadComponent: () =>
                            import(
                                '../settings/components/profile-form/profile-form.component'
                            ).then((c) => c.ProfileFormComponent),
                    },
                    {
                        path: 'change-password',
                        loadComponent: () =>
                            import(
                                '../settings/pages/change-password/change-password.component'
                            ).then((c) => c.ChangePasswordComponent),
                    },
                ],
            },
        ],
    },
];
